import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  ChangePasswordRequestDto,
  SendChangePasswordLinkRequestDto,
  SendChangePasswordLinkResponseDto,
  SendChangePasswordVerificationCodeResponseDto,
} from "./types";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import { MessageResultState } from "../../../../types/general-types";
import AccountActionStore from "./store";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import cx from "classnames";
import {
  FaSms,
  FaEnvelope,
  FaArrowRight,
  FaArrowLeft,
  FaSignInAlt,
  FaRedo,
} from "react-icons/fa";

interface IPage {
  AccountActionStore?: AccountActionStore;
  verificationCodeResponseData: SendChangePasswordVerificationCodeResponseDto;
  changePasswordRequestData: ChangePasswordRequestDto;
  isShow: boolean;
  toggleModal: any;
}

const Page: FC<IPage> = inject("AccountActionStore")(
  observer((props: IPage) => {
    const {
      AccountActionStore: store,
      verificationCodeResponseData,
      changePasswordRequestData,
      toggleModal,
      isShow,
    } = props;
    const { t } = useTranslation();
    const {
      register,
      formState,
      handleSubmit,
      errors,
      control,
      clearErrors,
      setValue,
      getValues,
    } = useForm<ChangePasswordRequestDto>({
      defaultValues: {
        password: changePasswordRequestData.password,
        passwordVerify: changePasswordRequestData.passwordVerify,
        key: changePasswordRequestData.key,
        verificationCode: "",
      },
    });
    register("password");
    register("passwordVerify");
    register("key");
    const [timer, setTimer] = useState<number>(
      verificationCodeResponseData.verificationSeconds
    );

    useEffect(() => {
      let interval: any = null;
      if (timer && timer > 0) {
        interval = setInterval(() => {
          setTimer((prev) => (prev ? prev - 1 : 0));
        }, 1000); // Her saniyede bir sayacı bir azalt
      } else {
        setValue("verificationCode", "");
        clearInterval(interval); // Zamanlayıcıyı durdur
      }

      return () => clearInterval(interval); // Component unmount olduğunda zamanlayıcıyı temizle
    }, [setValue, timer]);

    const sendVerificationCode = useCallback(async () => {
      if (store) {
        await store
          ?.SendChangePasswordVerificationCode(changePasswordRequestData)
          .then((res) => {
            if (res.state === MessageResultState.SUCCESS) {
              setValue("verificationCode", "");
              setTimer(res.result.verificationSeconds);
            }
          });
      }
    }, [store, changePasswordRequestData, setValue]);

    const checkChangePasswordVerificationCode = useCallback(async () => {
      if (store && getValues("verificationCode")) {
        await store
          ?.CheckChangePasswordVerificationCode(getValues())
          .then(async (res) => {
            if (res.state === MessageResultState.SUCCESS) {
              // setTimeout'ı bir Promise olarak sarıyoruz
              await new Promise<void>((resolve) => {
                setTimeout(() => {
                  window.location.href = window.location.origin + "/login";
                  resolve(); // işlemin tamamlandığını bildiriyoruz
                }, 3456); // 3456 milisaniye sonra yönlendirme yapılacak
              });
            }
          });
      }
    }, [getValues, store]);

    const VerificationCodePage = useMemo(() => {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center p-0">
          <div
            className="card w-100 shadow-lg border-0 "
            style={{ maxWidth: "400px", borderRadius: "15px" }}
          >
            <div className="card-body">
              <div className="card-title mb-4 fw-semibold d-flex justify-content-between align-items-center">
                <h5 className="text-center w-100 m-0">
                  {t("Verification Code")}{" "}
                  <span className="text-danger">*</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => toggleModal()}
                ></button>
              </div>

              {/* Doğrulama Kodu Input */}
              <div className="mb-4 d-flex justify-content-center">
                <Controller
                  name="verificationCode"
                  control={control}
                  rules={{
                    required: t("Please fill out this field."),
                    minLength: {
                      value: 4,
                      message: t(
                        "The given value must be {{value}} characters..",
                        { value: 4 }
                      ),
                    },
                    maxLength: {
                      value: 8,
                      message: t(
                        "The given value must be {{value}} characters.",
                        { value: 8 }
                      ),
                    },
                  }}
                  render={(field, state) => (
                    <>
                      <input
                        minLength={4}
                        maxLength={8}
                        type="text"
                        placeholder={t("Verification Code")}
                        style={{ width: "50%" }}
                        disabled={formState.isSubmitting || timer === 0}
                        {...field}
                        className={cx("form-control text-center ", {
                          "is-invalid": errors["verificationCode"],
                        })}
                        onKeyDown={(
                          event: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSubmit(async () => {
                              await checkChangePasswordVerificationCode();
                            })();
                          }
                        }}
                      />
                    </>
                  )}
                />
              </div>

              {/* Zamanlayıcı */}
              <div className="text-center mb-3">
                {timer && timer > 0 ? (
                  <span>
                    {t("Remaining Time:")} {timer} {t("seconds")}
                  </span>
                ) : (
                  <Button
                    className="btn btn-warning align-items-center"
                    onClick={sendVerificationCode}
                    icon={<FaRedo />}
                    tooltipText={t("Resend Code")}
                  />
                )}
              </div>

              <div className="text-center pt-4">
                <Button
                  className="btn btn-success align-items-center"
                  onClick={handleSubmit(async () => {
                    await checkChangePasswordVerificationCode();
                  })}
                  icon={<FaSignInAlt className="me-2" />}
                  text={t("Verify")}
                  disabled={
                    timer === undefined || timer === 0 || formState.isSubmitting
                  }
                />
              </div>
            </div>
          </div>
        </div>
      );
    }, [
      checkChangePasswordVerificationCode,
      control,
      errors,
      formState.isSubmitting,
      handleSubmit,
      sendVerificationCode,
      t,
      timer,
      toggleModal,
    ]);

    return (
      <Modal
        isOpen={isShow}
        toggle={() => toggleModal()}
        className="verify-modal"
      >
        <div>{VerificationCodePage}</div>
      </Modal>
    );
  })
);
export default Page;
